import type { ChatContextHooksFull } from '@/contexts/chat/context';
import { useChatContext } from '@/contexts/chat/context';
import { useAudio } from '@/scripts/hooks/useAudio';
import { isDebug } from '@/scripts/utils/devUtils';
import {
  ChatCategory,
  type Chat,
  type ChatParams,
} from '@/contexts/chat/scripts/hooks/useChats/types';
import {
  ChatMessagePayloadType,
  type SendMessageParams,
} from '@/contexts/chat/scripts/hooks/useChatMessages/types';
import { isValidMessage } from '@/scripts/utils/isValidMessage';
import type { ReplyMessage, SetReplyMessage } from '@/scripts/hooks/useReply';
import type { ChatGroup } from '@/contexts/chat/scripts/hooks/useChatGroup/types';
import ChatInputExtend from '../chat-input-extend';
import { Editor, useEditorContext } from '../chat-editor';
import classes from './index.module.scss';
import { Reply } from './components/reply';
import { deserializePayloadContent, handleKeyDown, serializePayload } from './helpers';

export interface ChatInputProps {
  className?: ClassNames;
  chat: Chat;
  chatGroup: ChatContextHooksFull['chatGroup'];
  replyMessage?: ReplyMessage;
  setReplyMessage?: SetReplyMessage;
  activatedChat?: Chat;
  group: ChatGroup;
  onSend: (value: SendMessageParams, chat: Chat) => void;
  updateChats: (chats: ChatParams[]) => Promise<void>;
}

export default function ChatInput({
  className,
  chat,
  chatGroup,
  replyMessage,
  setReplyMessage,
  activatedChat,
  group,
  onSend,
  updateChats,
}: ChatInputProps) {
  const {
    encryptMsg,
    fileMessages: { pickFilesUtil, getPickInfoEventHandlers },
  } = useChatContext();

  const { editor, focus: editorFocus, clear: editorClear } = useEditorContext();

  const [payload, setPayload] = useState('');
  const [mentionIds, setMentionIds] = useState<string[]>([]);

  const { play: playSendSound } = useAudio({ src: '/media/send.mp3' });

  const focus = useCallback(() => {
    editorFocus();
  }, [editorFocus]);

  const clear = useCallback(() => {
    setReplyMessage?.(null);
    editorClear();
  }, [editorClear, setReplyMessage]);

  const insertLB = useCallback(() => {
    editor.insertBreak();
  }, [editor]);

  const onSend_ = useCallback(
    (payload: string, params?: Partial<SendMessageParams>) => {
      const repeatArgs =
        isDebug &&
        /^\/repeat(?:\s(\d+))?(?:\s(.+))?/.exec(deserializePayloadContent(payload));
      if (repeatArgs) {
        const date = new Date().toLocaleString();

        let [, num, text] = repeatArgs;
        num = num ? Number(num) : 20;

        text = text || `Test text from test send 👽👽👽.`;

        onSend_(
          `------------------------------------ Start [${date}] ------------------------------------`
        );
        let j = 0;
        while (j < num) {
          onSend_(`[${j}] --- ${text}`);
          j++;
        }
        onSend_(
          `------------------------------------ End ------------------------------------`
        );
      } else {
        onSend(
          {
            payload_type:
              encryptMsg &&
              (group?.is_encryption || chat.category === ChatCategory.SINGLE)
                ? ChatMessagePayloadType.ENCRYPTED_TEXT
                : ChatMessagePayloadType.TEXT,
            ...params,
            payload,
          },
          chat
        );
      }
    },
    [chat, encryptMsg, group?.is_encryption, onSend]
  );

  const send = useCallback(() => {
    const param = {
      at_list: mentionIds.join(','),
    };

    if (replyMessage) {
      Object.assign(param, {
        link_msg_id: `${replyMessage.message?.message_id}_${replyMessage.message?.owner_id}_${replyMessage.message?.payload_type}`,
      });
    }

    if (isValidMessage(payload)) {
      playSendSound();
      onSend_(payload, param);
      clear();
    }
  }, [mentionIds, replyMessage, payload, playSendSound, onSend_, clear]);

  const eventHandlers = useMemo(
    () => getPickInfoEventHandlers(chat),
    [chat, getPickInfoEventHandlers]
  );

  useEffect(() => {
    if (chat.chat_id === activatedChat?.chat_id || replyMessage) {
      focus();
    }
  }, [activatedChat?.chat_id, chat.chat_id, focus, replyMessage]);

  return (
    <>
      <div
        className={cls(classes.chatInput, className, {
          [classes.dragging]: pickFilesUtil.isDragging,
        })}
        onClick={focus}
        {...{ ...eventHandlers.drop, ...eventHandlers.paste }}
      >
        {replyMessage && (
          <Reply
            chat={chat}
            chatGroup={chatGroup}
            replyMessage={replyMessage}
            setReplyMessage={setReplyMessage}
          />
        )}
        <div className={classes.inputTop}>
          <Editor
            className={cls(classes.input, 'scrollbar_hover')}
            placeholder={`Message ${chat.name}`}
            onKeyDown={(e) => {
              handleKeyDown(e, insertLB, send);
            }}
            onChange={(_, editor) => {
              const { payload, mentionIds } = serializePayload(editor);
              setPayload(payload);
              setMentionIds(mentionIds);
            }}
          ></Editor>
          <ChatInputExtend
            payload={payload}
            chat={chat}
            updateChats={updateChats}
            onSend={send}
          />
        </div>
      </div>
    </>
  );
}
